import type { AxiosProgressEvent } from "axios";
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import organizationService from "~/modules/organization/services/organization.service";
import type { RelationReferentUser } from "~/modules/relation/models/RelationReferentUser.interface";

export class RelationService extends RestService {
  protected RELATIONS = "relations";
  protected REFERENT_USERS = "referentUsers";

  getBusinessUnitReferentUser({
    businessUnitCode,
  }: GenericParameters): Promise<RelationReferentUser> {
    return this.$apiV2.get(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getReferentUserPath()}`,
    );
  }

  getOrganizationReferentUser({
    organizationCode,
  }: GenericParameters): Promise<RelationReferentUser> {
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getReferentUserPath()}`,
    );
  }

  updateBusinessUnitReferentUser({
    businessUnitCode,
    referentUserLogin,
  }: GenericParameters & { referentUserLogin: string }): Promise<void> {
    return this.$apiV2.put(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getReferentUserPath()}/${referentUserLogin}`,
    );
  }

  updateOrganizationReferentUser({
    organizationCode,
    referentUserLogin,
  }: GenericParameters & { referentUserLogin: string }): Promise<void> {
    return this.$apiV2.put(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getReferentUserPath()}/${referentUserLogin}`,
    );
  }

  deleteBusinessUnitReferentUser({
    businessUnitCode,
  }: GenericParameters): Promise<void> {
    return this.$apiV2.delete(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getReferentUserPath()}`,
    );
  }

  deleteOrganizationReferentUser({
    organizationCode,
  }: GenericParameters): Promise<void> {
    return this.$apiV2.delete(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getReferentUserPath()}`,
    );
  }

  createUploadFormData(file: File): FormData {
    const formData = new FormData();
    formData.append("platformIdCardFile", file);
    return formData;
  }

  createUploadConfig(progress: Ref<number | undefined | null>): {
    headers: { "Content-Type": string };
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
    timeout: number;
  } {
    return {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        progress.value = progressEvent.total
          ? (progressEvent.loaded / progressEvent.total) * 100
          : 0;
      },
      timeout: Infinity,
    };
  }

  getRelationsPath() {
    return this.RELATIONS;
  }

  getReferentUserPath() {
    return `${this.getRelationsPath()}/${this.REFERENT_USERS}`;
  }
}
export default new RelationService();
